import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [services, setServices] = useState([]);

  // Fetch services on component mount
  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await axios.get(`${API}/services`);
        setServices(response.data);
      } catch (error) {
        console.error('Error fetching services:', error);
      }
    };

    fetchServices();
  }, []);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contact-submissions`, formData);
      
      if (response.data.id) {
        toast.success('Consultation request submitted! We will contact you within 24 hours.');
        setFormData({
          name: '',
          email: '',
          company: '',
          service: '',
          message: ''
        });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      
      // Enhanced error tracking and user feedback
      let errorMessage = 'Failed to submit consultation request. Please try again.';
      let errorDetails = {
        timestamp: new Date().toISOString(),
        formData: formData,
        error: error.message,
        userAgent: navigator.userAgent
      };
      
      if (error.response?.status) {
        errorDetails.statusCode = error.response.status;
        errorDetails.statusText = error.response.statusText;
        
        if (error.response.status === 500) {
          errorMessage = 'Server error occurred. Our team has been notified. Please try again in a few minutes.';
        } else if (error.response.status === 422) {
          errorMessage = 'Please check your form data and try again.';
        } else if (error.response.status === 0 || !navigator.onLine) {
          errorMessage = 'Network connection issue. Please check your internet and try again.';
        }
      }
      
      // Log error details to console for debugging
      console.error('Form submission error details:', errorDetails);
      
      // Store error in localStorage for admin tracking
      const errors = JSON.parse(localStorage.getItem('aquasage_form_errors') || '[]');
      errors.push(errorDetails);
      // Keep only last 10 errors
      localStorage.setItem('aquasage_form_errors', JSON.stringify(errors.slice(-10)));
      
      if (error.response?.data?.detail) {
        toast.error(`Error: ${error.response.data.detail}`);
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="dark-container py-20">
      <div className="dark-content-container">
        <div className="text-center mb-16">
          <h2 className="display-large mb-6" style={{color: 'var(--text-primary)'}}>
            Ready to Transform Your Maritime Asset?
          </h2>
          <p className="body-large text-text-secondary max-w-3xl mx-auto">
            Get in touch with our experts to discuss your maritime digital transformation needs.
          </p>
        </div>
        
        <div className="grid lg:grid-cols-2 gap-16">
          {/* Contact Info */}
          <div className="space-y-8">
            <div>
              <h3 className="heading-2 text-white mb-6">Get In Touch</h3>
              <p className="body-medium text-text-secondary mb-8">
                Comprehensive maritime solutions for vessel optimization, digital transformation, and sustainable operations.
              </p>
            </div>
            
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-brand-hover rounded-lg flex items-center justify-center">
                  <Mail className="text-brand-primary" size={20} />
                </div>
                <div>
                  <div className="body-medium" style={{color: 'var(--text-primary)'}}>contact@aquasagegroup.com</div>
                  <div className="body-small text-text-muted">Email us anytime</div>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-brand-hover rounded-lg flex items-center justify-center">
                  <Phone className="text-brand-primary" size={20} />
                </div>
                <div>
                  <div className="body-medium" style={{color: 'var(--text-primary)'}}>+85257213165, +85257213156, +85255093700</div>
                  <div className="body-small text-text-muted">Call for immediate assistance</div>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-brand-hover rounded-lg flex items-center justify-center">
                  <MapPin className="text-brand-primary" size={20} />
                </div>
                <div>
                  <div className="body-medium" style={{color: 'var(--text-primary)'}}>Hong Kong</div>
                  <div className="body-small text-text-muted">Strategic maritime hub location</div>
                </div>
              </div>
            </div>
            
            <div className="pt-8">
              <img 
                src="https://images.unsplash.com/photo-1715183502199-c2449fbb5681?w=600&h=256&fit=crop&auto=format"
                alt="Maritime Technology"
                className="w-full h-64 object-cover rounded-lg"
                loading="lazy"
              />
            </div>
          </div>
          
          {/* Contact Form */}
          <Card className="bg-bg-secondary border-border-subtle">
            <CardHeader>
              <CardTitle className="heading-2" style={{color: 'var(--text-primary)'}}>Request Consultation</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name" className="text-text-primary">Full Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="bg-white border-2 border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                      style={{color: '#1A365D'}}
                    />
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-text-primary">Email *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="bg-white border-2 border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                      style={{color: '#1A365D'}}
                    />
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="company" className="text-text-primary">Company</Label>
                  <Input
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    className="bg-white border-2 border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                    style={{color: '#1A365D'}}
                  />
                </div>
                
                <div>
                  <Label htmlFor="service" className="text-text-primary">Service of Interest</Label>
                  <select
                    id="service"
                    name="service"
                    value={formData.service}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 bg-white border-2 border-gray-300 rounded-md focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                    style={{color: '#1A365D'}}
                  >
                    <option value="">Select a service</option>
                    {services.map((service) => (
                      <option key={service.id} value={service.title}>
                        {service.title}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <Label htmlFor="message" className="text-text-primary">Message *</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows={4}
                    className="bg-white border-2 border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                    style={{color: '#1A365D'}}
                    placeholder="Tell us about your maritime challenges and goals..."
                  />
                </div>
                
                <Button 
                  type="submit" 
                  className="btn-primary w-full"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Submitting...' : 'Send Consultation Request'}
                  <Send size={18} />
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default Contact;